import PageHeader from "../../../components/layout/PageHeader";
import PageContent from "../../../components/layout/PageContent";
import {useEffect, useState} from "react";
import FailureReport from "../../../api/failure/report/FailureReport";
import FailureReportApi, {SearchQuery} from "../../../api/failure/report/FailureReportApi";
import {showNotification} from "@mantine/notifications";
import Page from "../../../models/api/Page";
import {DataTable, DataTableColumn, useDataTableColumns} from "mantine-datatable";
import CreateFailureReportDialog from "./CreateFailureReportDialog";
import {Anchor, Button, Flex, Group, TextInput} from "@mantine/core";
import {Api} from "../../../api/Api";
import {IconPencil} from "@tabler/icons-react";
import {useContextMenu} from "mantine-contextmenu";
import UpdateFailureReportDialog from "./UpdateFailureReportDialog";

const key = 'FailureReport-StoreKey';


/**
 * Converts a date string from the "YYYY-MM-DD HH:mm:ss" format to a human-readable string.
 * For example: "2025-04-03 00:00:00" -> "3 kwietnia 2025, 00:00".
 *
 * @param dateTimeString - The date string in "YYYY-MM-DD HH:mm:ss" format.
 * @returns A human-readable formatted date string.
 */
function convertDate(dateTimeString: string | null | undefined): string {
    if (dateTimeString === null || dateTimeString === undefined) {
        return "-";
    }

    const months = [
        "stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca",
        "lipca", "sierpnia", "września", "października", "listopada", "grudnia"
    ];

    try {
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);

        const humanReadableDate = `${day} ${months[month - 1]} ${year}`;
        const humanReadableTime = timePart.slice(0, 5); // Only HH:mm

        return `${humanReadableDate}, ${humanReadableTime}`;
    } catch (error) {
        console.error("Invalid date format:", dateTimeString);
        return "Nieprawidłowy format daty";
    }
}

function FailureReportPage() {
    const [query, setQuery] = useState<SearchQuery>({limit: 50, page: 0});
    const [data, setData] = useState<Page<FailureReport>>();
    const [selectedRecords, setSelectedRecords] = useState<FailureReport[]>([]);
    const [createDialog, setCreateDialog] = useState(false);
    const [edit, setEdit] = useState<FailureReport>();
    const { showContextMenu } = useContextMenu();

    const columns: DataTableColumn<FailureReport>[] = [
        {
            accessor: 'id',
            title: '_id',
            toggleable: true,
            defaultToggle: false,
            sortable: true
        },
        {
            accessor: 'unit',
            title: '#',
            toggleable: true,
            sortable: true,
            draggable: true
        },
        {
            accessor: 'title',
            title: 'Nazwa raportu'
        },
        {
            accessor: 'dateTime',
            title: 'Data',
            toggleable: true,
            sortable: true,
            draggable: true,
            render: r => (r.dateTime as any as string),
        },
        {
            accessor: 'relatedDevice',
            title: 'Powiązane urządzenie',
            toggleable: true,
            sortable: false,
            draggable: true,
            render: r => r.relatedDevice?.name
        },
        {
            accessor: 'storeVaccinesFrom',
            title: 'Przechowywanie szczepionek (Od)',
            render: r => convertDate(r.storeVaccinesFrom as any as string),
            toggleable: true,
            sortable: true,
            draggable: true
        },
        {
            accessor: 'storeVaccinesTo',
            title: 'Przechowywanie szczepionek (Do)',
            render: r => convertDate(r.storeVaccinesTo as any as string),
            toggleable: true,
            sortable: true,
            draggable: true
        },
        {
            accessor: 'storeVaccinesPlace',
            title: 'Miejsce przechowywania szczepionek',
            toggleable: true,
            sortable: true,
            draggable: true
        },
        {
            accessor: 'fromDate',
            title: 'Czas trwania (Od)',
            toggleable: true,
            draggable: true,
            render: ({fromDate}) => convertDate(fromDate as any as string)
        },
        {
            accessor: 'toDate',
            title: 'Czas trwania (Do)',
            toggleable: true,
            draggable: true,
            render: ({toDate}) => convertDate(toDate as any as string)
        },
        {
            accessor: 'Raport',
            title: 'Raport',
            toggleable: true,
            draggable: true,
            render: ({compositeReport}) => (
                <>{compositeReport && compositeReport.title}</>
            )
        }
    ]

    const {effectiveColumns} = useDataTableColumns({key, columns})

    const reload = () => {
        let searchQuery = {
            ...query,
            unit: query.unit === "" ? undefined : query.unit
        };
        FailureReportApi.search(searchQuery)
            .then(setData)
            .catch(() => showNotification({message: 'Nie udało się pobrać danych'}))
    }

    const deleteSelected = () => {
        if (selectedRecords.length === 0) {
            return;
        }

        FailureReportApi.deleteAll(selectedRecords.map(it => it.id))
            .then(() => showNotification({message: 'Usunięto zaznaczone elementy'}))
            .then(reload)
            .then(() => setSelectedRecords([]))
            .catch(() => showNotification({message: 'Nie udało się usunąć zaznaczonych elementów', color: 'red'}))
    }

    useEffect(reload, [query]);

    return (
        <>
            <CreateFailureReportDialog
                isOpen={createDialog}
                onClose={() => setCreateDialog(false)}
                closeAndUpdate={() => {
                    setCreateDialog(false);
                    reload();
                }}
            />

            <UpdateFailureReportDialog
                failureReport={edit}
                onClose={() => {
                    setEdit(undefined);
                }}
                closeAndUpdate={() => {
                    setEdit(undefined);
                    reload();
                }}
            />

            <PageHeader
                title="Raporty z interwencji"
                pageTitle="Raporty z interwencji"
                url="/failure/report"
                action={
                    <Group>
                        <Button onClick={deleteSelected} color="red">
                            Usuń zaznaczone
                        </Button>

                        <Button onClick={() => setCreateDialog(true)}>
                            Utwórz
                        </Button>
                    </Group>
                }
            />

            <PageContent>
                <Flex direction="row" align="center" gap={'md'}>
                    <TextInput
                        placeholder="Jednostka"
                        onChange={ev => {
                            query.unit = ev.target.value
                        }}
                    />

                    <Button onClick={() => reload()}>Szukaj</Button>
                </Flex>

                <DataTable
                    mt="xl"
                    striped
                    height="60vh"
                    withColumnBorders
                    styles={{header: {background: 'white'}}}
                    highlightOnHover
                    loaderBackgroundBlur={1}
                    backgroundColor="transparent"
                    noRecordsText="Pusto..."
                    columns={effectiveColumns}
                    records={data?.content}
                    selectedRecords={selectedRecords}
                    onSelectedRecordsChange={setSelectedRecords}
                    minHeight={150}
                    style={{marginBottom: '50px'}}
                    storeColumnsKey={key}
                    onRowContextMenu={(data) => {
                        showContextMenu([
                            {
                                title: 'Edycja',
                                icon: <IconPencil size={16}/>,
                                key: 'edit-fr',
                                onClick: () => setEdit(data.record),
                            },
                        ])(data.event)
                    }
                    }
                />
            </PageContent>
        </>
    );
}

export default FailureReportPage;